/**
 * WHAT THE SCREEN UNDER THE CHROME LOOKS LIKE — the one rule the Studio bar and the left nav both follow. Pure — no Angular.
 *
 * Bastion is always white, whatever the switch says. Pages, Entities, Users & roles and Users & workspaces follow the
 * sun/moon switch (`GlobalVariablesService.pageThemeDark`, dark by default). Every other screen keeps the look it has
 * always had, which neither the bar nor the nav is told, so it has no tone here and each keeps its own default.
 *
 * ⚠ ONE RULE, TWO READERS. The bar goes white on a white screen; the nav goes white on a dark one, so it never sinks into
 * the page. Both ask this file, so a screen moved between the groups below moves for both at once.
 */
export type ScreenKind = 'bastion' | 'switchable' | 'other';
export type ScreenTone = 'light' | 'dark' | null;

/** The left nav's own names for the switchable screens (`detectCurrentRoute` in left-nav.component.ts). */
const SWITCHABLE_NAV_ROUTES: readonly string[] = ['pages', 'entities', 'userandroles', 'usersworkspaces'];

/** A screen, by the name the left nav keeps for it. */
export function screenKindOfNavRoute(route: string): ScreenKind {
  if (route === 'bastion') return 'bastion';
  return SWITCHABLE_NAV_ROUTES.includes(route) ? 'switchable' : 'other';
}

/** A screen, by its router URL. Only the path counts: a query or a fragment never changes the screen. */
export function screenKindOfUrl(url: string): ScreenKind {
  const path = url.split(/[?#]/)[0];
  if (/^\/dashboard\/bastion(\/|$)/.test(path)) return 'bastion';
  if (/^\/dashboard\/(pages|entity-list|entity-detail|user-mgmt|users-workspaces)(\/|$)/.test(path)) return 'switchable';
  return 'other';
}

/** The tone of a screen of this kind, or null where the screen's look is its own. */
export function screenTone(kind: ScreenKind, pageThemeDark: boolean): ScreenTone {
  if (kind === 'bastion') return 'light';
  if (kind === 'switchable') return pageThemeDark ? 'dark' : 'light';
  return null;
}

/** Whether the Studio bar on this URL is the white one. Anywhere without a known tone keeps today's dark bar. */
export function isLightScreen(url: string, pageThemeDark: boolean): boolean {
  return screenTone(screenKindOfUrl(url), pageThemeDark) === 'light';
}
